import { useEffect, useRef } from 'react';

/*
 * Gaze-tracking face.
 * Implements /public/hero/hero-gaze-tracking-spec.md.
 *
 * Asset canvas is 1134 x 1571. All four layers share it with no cropping.
 * Layer stack (bottom → top):
 *   1. face-base.png   — head + neck, blanked sclera
 *   2. iris.png        — clipped by the eye-white paths (rendered inside an
 *                        SVG so the userSpaceOnUse clip scales with the layout)
 *   3. eyelid.png      — blink layer, opacity 0 by default, cross-faded in
 *
 * The head wrapper gets the slow spring (translate + 3D rotation); the iris
 * group gets the fast spring, so the eyes arrive before the head follows.
 */

const AW = 1134;   // asset width
const AH = 1571;   // asset height

// Ranges, expressed as a fraction of asset width (spec § Ranges).
// Both irises translate by the SAME delta, so the gaze stays parallel
// (both eyes point the same way) rather than diverging.
const IRIS_X = 0.0205 * AW; // ~23px — near the 24px ceiling so extreme gaze
                           // reads as genuinely looking to the side
const IRIS_Y = 0.006 * AW;  // ~7px
const HEAD_X = 0.016;       // × rendered width
const HEAD_Y = 0.009;       // × rendered width
const HEAD_ROT_Y = 12;      // deg — head turns further toward the cursor
const HEAD_ROT_X = 6;       // deg
const HEAD_ROT_Z = 1.3;     // deg — very subtle tilt in the cursor's direction

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function GazeFace({ className = '', style }) {
  const stageRef = useRef(null);  // stable rect for pointer mapping
  const headRef = useRef(null);   // slow spring target
  const irisRef = useRef(null);   // fast spring target (an SVG <g>)
  const lidRef = useRef(null);    // blink layer

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return; // static face-base only

    const canHover = window.matchMedia('(hover: hover)').matches;
    const stage = stageRef.current;
    const head = headRef.current;
    const iris = irisRef.current;

    // ── motion state ──
    const target = { x: 0, y: 0 };
    const irisS = { x: 0, y: 0 };
    const headS = { x: 0, y: 0 };
    // rafId is the single source of truth: 0 = loop stopped.
    let rafId = 0;

    // ── idle drift (slow Lissajous) ──
    let lastMove = performance.now();
    const IDLE_DELAY = 4000;
    const IDLE_AMP = 0.15;

    const setWillChange = (on) => {
      head.style.willChange = on ? 'transform' : 'auto';
      iris.style.willChange = on ? 'transform' : 'auto';
    };

    const loop = () => {
      const now = performance.now();
      const idling = now - lastMove > IDLE_DELAY;

      // idle drift keeps a slight wandering attention after 4s of stillness
      if (idling) {
        const t = now / 1000;
        target.x = Math.sin(t * 0.34) * IDLE_AMP;
        target.y = Math.sin(t * 0.21 + 1.3) * IDLE_AMP;
      }

      irisS.x += (target.x - irisS.x) * 0.14;
      irisS.y += (target.y - irisS.y) * 0.14;
      headS.x += (target.x - headS.x) * 0.06;
      headS.y += (target.y - headS.y) * 0.06;

      const w = stage.getBoundingClientRect().width || AW;
      iris.setAttribute(
        'transform',
        `translate(${(irisS.x * IRIS_X).toFixed(2)} ${(irisS.y * IRIS_Y).toFixed(2)})`
      );
      head.style.transform =
        `translate(${(headS.x * HEAD_X * w).toFixed(2)}px, ${(headS.y * HEAD_Y * w).toFixed(2)}px)` +
        ` rotateY(${(headS.x * HEAD_ROT_Y).toFixed(2)}deg)` +
        ` rotateX(${(-headS.y * HEAD_ROT_X).toFixed(2)}deg)` +
        ` rotateZ(${(headS.x * HEAD_ROT_Z).toFixed(2)}deg)`;

      const settled =
        Math.abs(target.x - irisS.x) < 0.001 &&
        Math.abs(target.y - irisS.y) < 0.001 &&
        Math.abs(target.x - headS.x) < 0.001 &&
        Math.abs(target.y - headS.y) < 0.001;

      // Stop only when settled and not idling; idle drift keeps the loop alive.
      if (settled && !idling) {
        rafId = 0;
        setWillChange(false);
        return;
      }
      rafId = requestAnimationFrame(loop);
    };

    const kick = () => {
      if (rafId) return;
      setWillChange(true);
      rafId = requestAnimationFrame(loop);
    };

    const onMove = (e) => {
      lastMove = performance.now();
      const rect = stage.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      target.x = clamp((e.clientX - cx) / (window.innerWidth / 2), -1, 1);
      target.y = clamp((e.clientY - cy) / (window.innerHeight / 2), -1, 1);
      kick();
    };

    if (canHover) {
      window.addEventListener('pointermove', onMove, { passive: true });
    } else {
      // touch devices keep blink + idle drift, no pointer tracking
      lastMove = -Infinity; // start idling immediately
      kick();
    }

    // ── blink scheduler ──
    let blinkTimer = 0;
    const blinkOnce = () => {
      const el = lidRef.current;
      if (!el) return; // eyelid missing → blink is a no-op, everything else works
      el.style.transition = 'opacity 60ms linear';
      el.style.opacity = '1';
      window.setTimeout(() => {
        el.style.transition = 'opacity 80ms linear';
        el.style.opacity = '0';
      }, 60 + 40); // fade-in + hold
    };
    const scheduleBlink = () => {
      const delay = 3200 + Math.random() * 3300; // 3.2s–6.5s
      blinkTimer = window.setTimeout(() => {
        blinkOnce();
        if (Math.random() < 1 / 6) window.setTimeout(blinkOnce, 180 + 60); // double blink
        scheduleBlink();
      }, delay);
    };
    scheduleBlink();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.clearTimeout(blinkTimer);
      window.removeEventListener('pointermove', onMove);
      setWillChange(false);
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className={`gaze-stage ${className}`}
      style={style}
      aria-label="Portrait of Muneeb Khan"
      role="img"
    >
      <div ref={headRef} className="gaze-head">
        <img
          className="gaze-layer gaze-base"
          src="/hero/face-base.png"
          alt="Muneeb Khan"
          decoding="async"
        />

        <svg
          className="gaze-layer gaze-iris"
          viewBox={`0 0 ${AW} ${AH}`}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            {/* Feathered mask instead of a hard clip: the soft edge hides the
                seam where iris.png's retouch differs from face-base, and lets the
                iris fade out near a corner instead of being sliced (which made the
                two eyes read as pointing at different angles). Each eye polygon is
                inset ~10% toward its own centre so the reveal stays inside the
                clean eye opening. */}
            <filter id="gaze-feather" x="-40%" y="-120%" width="180%" height="340%">
              <feGaussianBlur stdDeviation="5" />
            </filter>
            <mask id="gaze-eyes-mask" maskUnits="userSpaceOnUse" x="0" y="0" width={AW} height={AH}>
              <g fill="#fff" filter="url(#gaze-feather)">
                <g transform="translate(348 808) scale(0.9) translate(-348 -808)">
                  <polygon points="339.0,851.2 325.0,851.4 304.0,848.2 286.0,843.2 271.0,837.1 266.0,834.2 262.0,831.4 254.0,823.5 250.8,819.0 250.8,813.0 253.9,804.0 256.8,799.0 261.0,793.5 274.5,780.0 284.0,772.8 294.0,767.8 302.0,764.8 310.0,762.6 318.0,762.6 321.0,763.4 326.0,763.6 336.0,765.4 341.0,765.6 351.0,767.4 356.0,767.6 366.0,769.4 378.0,770.6 408.0,775.6 412.0,776.9 417.0,780.5 431.0,794.5 436.0,800.7 441.3,810.0 445.2,820.0 445.4,829.0 444.2,831.0 428.0,837.2 392.0,846.4 385.0,847.4 376.0,847.6 373.0,848.4 365.0,848.6 362.0,849.4 353.0,849.6 350.0,850.4 341.0,850.6 339.0,851.2" />
                </g>
                <g transform="translate(788 806) scale(0.9) translate(-788 -806)">
                  <polygon points="817.0,850.2 798.0,850.4 795.0,849.6 788.0,849.4 785.0,848.6 778.0,848.4 775.0,847.6 768.0,847.4 765.0,846.6 758.0,846.4 755.0,845.6 748.0,845.4 741.0,844.2 735.0,842.2 720.0,835.2 712.0,830.2 706.0,825.4 702.6,822.0 693.8,810.0 692.6,807.0 692.8,805.0 696.0,799.7 705.0,788.5 713.0,780.6 719.0,775.8 727.0,770.8 735.0,769.6 742.0,769.4 745.0,768.6 752.0,768.4 755.0,767.6 762.0,767.4 765.0,766.6 772.0,766.4 775.0,765.6 782.0,765.4 785.0,764.6 792.0,764.4 795.0,763.6 802.0,763.4 805.0,762.6 812.0,762.4 816.0,761.5 823.0,761.6 834.0,764.8 846.0,769.7 861.0,778.8 875.5,793.0 879.2,798.0 883.2,806.0 884.4,810.0 884.4,816.0 882.7,821.0 879.4,826.0 875.0,830.4 865.0,837.3 854.0,842.2 845.0,845.2 830.0,848.4 817.0,850.2" />
                </g>
              </g>
            </mask>
          </defs>
          {/* Mask on a fixed wrapper; the iris translates inside it. */}
          <g mask="url(#gaze-eyes-mask)">
            <g ref={irisRef}>
              <image href="/hero/iris.png" x="0" y="0" width={AW} height={AH} />
            </g>
          </g>
        </svg>

        <img
          ref={lidRef}
          className="gaze-layer gaze-lid"
          src="/hero/eyelid.png"
          alt=""
          aria-hidden="true"
          decoding="async"
          loading="lazy"
        />
      </div>
    </div>
  );
}
